import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ActiveDeviceModel } from "app/models/activeDevice.model";
import { DeviceFileModel, DeviceModel } from "app/models/device.model";
import { DeviceFilesRequest } from "./interfaces/request.interface";
import { PaginationResponse } from "./interfaces/response.interface";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { ApiService } from "./api.service";

export interface DeviceFileStorageRescanResult {
    scannedDevices: number;
    scannedObjects: number;
    recoveredFiles: number;
    skippedFiles: number;
}

@Injectable()
export class DeviceService extends ApiService {
    constructor(protected http: HttpClient) {
        super(http);
        this.apiEndpoint = "device";
    }

    get(id: string): Observable<DeviceModel> {
        return this.http.get<DeviceModel>(this.getUrl(id));
    }

    getAll(parameters?: object): Observable<DeviceModel[]> {
        return this.http.get<DeviceModel[]>(this.getUrl(""), parameters);
    }

    geActivated(id: string): Observable<ActiveDeviceModel> {
        const url = `${id}/activated`;
        return this.http.get<ActiveDeviceModel>(this.getUrl(url));
    }

    create(parameters: DeviceModel): Observable<DeviceModel> {
        return this.http.post<DeviceModel>(this.getUrl(""), parameters);
    }

    edit(parameters: DeviceModel): Observable<DeviceModel> {
        return this.http.put<DeviceModel>(this.getUrl(""), parameters);
    }

    delete(id: string): Observable<DeviceModel> {
        return this.http.delete<DeviceModel>(this.getUrl(String(id)));
    }

    addImage(id: string, image: any) {
        const url = `${id}/image`;
        const formData: FormData = new FormData();
        formData.append("image", image, image.name);
        return this.http.post(this.getUrl(url), formData);
    }

    removeImage(id: string) {
        const url = `${id}/image`;
        return this.http.delete(this.getUrl(url));
    }

    getFiles(id: string, request: DeviceFilesRequest): Observable<PaginationResponse<DeviceFileModel>> {
        const url = `${id}/files`;
        return this.http
            .get<PaginationResponse<DeviceFileModel>>(this.getUrl(url), {
                params: request as any,
            })
            .pipe(
                map((response) => ({
                    ...response,
                    data: response.data.map((file) => ({
                        ...file,
                        originalDate: file.originalDate ? new Date(file.originalDate) : undefined,
                        uploadDate: file.uploadDate ? new Date(file.uploadDate) : undefined,
                    })),
                })),
            );
    }

    getFileDownloadUrl(id: string, fileId: string) {
        const url = `${id}/file/${fileId}/download-url`;
        return this.http.get<{ url: string }>(this.getUrl(url));
    }

    reprocessFile(id: string, fileId: string): Observable<DeviceFileModel> {
        const url = `${id}/file/${fileId}/reprocess`;
        return this.http.post<DeviceFileModel>(this.getUrl(url), {});
    }

    rescanFileStorage(): Observable<DeviceFileStorageRescanResult> {
        return this.http.post<DeviceFileStorageRescanResult>(this.getUrl("files/rescan-storage"), {});
    }

    downloadFilesZip(id: string, fileIds: string[]) {
        const url = `${id}/files/download-zip`;
        return this.http.post(this.getUrl(url), { fileIds }, { observe: "response", responseType: "blob" }) as Observable<
            HttpResponse<Blob>
        >;
    }
}
